import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { fetchHealth } from "@/api/meta";
import { Badge, Card } from "@/components";
import { useLang } from "@/i18n/LanguageContext";
import { useAuthStore } from "@/state/auth.store";

export function HomePage() {
  const { t } = useLang();
  const demo = useAuthStore((s) => s.demo);
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  const health = useQuery({ queryKey: ["health"], queryFn: ({ signal }) => fetchHealth(signal) });
  const apiOnline = health.isSuccess && health.data.status === "ready";

  // Public demo: skip the landing and drop the visitor straight into the program.
  if (demo && user) return <Navigate to="/app" replace />;
  if (demo && !hydrated) return <div className="center-col">{t("Demo laden…", "Loading demo…")}</div>;

  return (
    <>
      <section className="app-hero">
        <div className="wrap inner on-dark">
          <p className="eyebrow">{t("Onboarding & kennisbank", "Onboarding & knowledge base")}</p>
          <h1>
            {t("Persoonlijke onboarding, ", "Personalized onboarding, ")}
            <span className="amber">{t("afgestemd op de functie", "tailored to the role")}</span>.
          </h1>
          <p>
            {t(
              "Eén kennisbank, slim samengesteld per medewerker: documenten, regels, workflows en processen — met voortgang, akkoord en kennischecks. Flexibel in te richten per bedrijf.",
              "One knowledge base, assembled per employee: documents, rules, workflows and processes — with progress, sign-off and knowledge checks. Configurable per company.",
            )}
          </p>
          <div className="meta-row">
            <Link to="/signup" className="btn btn-primary">{t("Gratis proberen", "Start free trial")}</Link>
            <Link to="/login" className="btn btn-outline">{t("Inloggen", "Log in")}</Link>
            {apiOnline ? (
              <Badge tone="ok"><span className="status-dot status-dot--ok" /> API online</Badge>
            ) : (
              <Badge tone="err"><span className="status-dot status-dot--err" /> API offline</Badge>
            )}
          </div>
        </div>
      </section>

      <section className="bg-soft">
        <div className="wrap">
          <div className="section-head">
            <p className="eyebrow">{t("Hoe het werkt", "How it works")}</p>
            <h2>{t("Modules + doelgroepen = persoonlijk traject", "Modules + audiences = a personal journey")}</h2>
          </div>
          <div className="cards">
            <Card>
              <p className="card-num">01</p>
              <h3>{t("Bouw modules", "Build modules")}</h3>
              <p>{t("Schrijf documenten, regels en workflows in Markdown — met variabelen die per medewerker worden ingevuld.", "Write documents, rules and workflows in Markdown — with variables filled in per employee.")}</p>
            </Card>
            <Card>
              <p className="card-num">02</p>
              <h3>{t("Richt doelgroepen in", "Define audiences")}</h3>
              <p>{t("Segmenteer op functie, afdeling, team of locatie. Trajecten koppelen content automatisch aan de juiste mensen.", "Segment by role, department, team or location. Journeys attach content to the right people automatically.")}</p>
            </Card>
            <Card>
              <p className="card-num">03</p>
              <h3>{t("Volg & borg", "Track & assure")}</h3>
              <p>{t("Voortgang, 'gelezen en begrepen'-akkoord en kennischecks — met een volledige audittrail.", "Progress, 'read and understood' sign-off and knowledge checks — with a full audit trail.")}</p>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
}
